import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { ConversationDetail } from '@/api';
import { useApi } from '@/api/provider';
import { STALE_TIMES } from '@/api/queryClient';
import { conversationKeys } from './queryKeys';

/**
 * The conversation the opened message belongs to
 * (`GET /accounts/{id}/messages/{messageId}/conversation`, plan/16 §2).
 *
 * - **No blank while switching.** Opening the next conversation keeps the
 *   previous one on screen until the answer arrives (`isPlaceholderData`
 *   says so), the same promise the list makes. Only within one account: a
 *   conversation of mailbox A must never sit under mailbox B's toolbar.
 * - **Freshness.** A conversation gains mail and changes flags, so it is a
 *   list-like answer (`STALE_TIMES.list`), unlike the immutable bodies of its
 *   messages, which come from the message detail query one by one.
 */
export function useConversation(
  accountId: string | undefined,
  messageId: string | undefined,
): UseQueryResult<ConversationDetail> {
  const api = useApi();
  return useQuery({
    queryKey: conversationKeys.detail(accountId ?? '', messageId ?? ''),
    queryFn: ({ signal }) => api.messages.conversation(accountId ?? '', messageId ?? '', signal),
    enabled: accountId !== undefined && accountId !== '' && messageId !== undefined && messageId !== '',
    staleTime: STALE_TIMES.list,
    placeholderData: (previous) =>
      previous !== undefined && previous.conversation.accountId === accountId ? previous : undefined,
  });
}
